import { UsersIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute } from '@/lib/format'

import { itemsOf, useStaffAccounts } from '../api'
import { LoadMore, StaffGate, StateBadge } from '../ui'
import { SELECT_CLASS } from '../shared'

export function StaffAccountsPage() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [state, setState] = useState('')
  const [applied, setApplied] = useState({ q: '', state: '' })
  const list = useStaffAccounts(applied.q, applied.state)
  const rows = itemsOf(list.data)

  return (
    <StaffGate>
      <PageHeader eyebrow="Staff" title="Accounts" description="Search by name, slug, a member's email, or an exact id." />
      <PageBody>
        <Card>
          <CardContent className="space-y-4">
            <form
              className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem_auto]"
              onSubmit={(e) => {
                e.preventDefault()
                setApplied({ q, state })
              }}
            >
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="acc-q">Search</Label>
                <Input id="acc-q" value={q} onChange={(e) => { setQ(e.target.value) }} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="acc-state">State</Label>
                <select id="acc-state" className={SELECT_CLASS} value={state} onChange={(e) => { setState(e.target.value) }}>
                  <option value="">Any</option>
                  <option value="active">Active</option>
                  <option value="deactivated">Deactivated</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit">Search</Button>
              </div>
            </form>

            {list.isError ? (
              <ErrorState error={list.error} title="Could not load accounts" onRetry={() => void list.refetch()} />
            ) : list.isPending ? (
              <TableSkeleton rows={6} columns={6} />
            ) : rows.length === 0 ? (
              <EmptyState icon={UsersIcon} title="No accounts match" description="Try another search or clear the state filter." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead className="text-right">Members</TableHead>
                        <TableHead className="text-right">Zones</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((a) => (
                        <TableRow
                          key={a.id}
                          data-testid="staff-account-row"
                          className="cursor-pointer"
                          onClick={() => { void navigate(`/app/staff/accounts/${a.id}`) }}
                        >
                          <TableCell className="max-w-[16rem] truncate font-medium">{a.name}</TableCell>
                          <TableCell className="font-mono text-xs">{a.slug}</TableCell>
                          <TableCell><StateBadge state={a.account_state} /></TableCell>
                          <TableCell className="text-xs">{a.plan}</TableCell>
                          <TableCell className="text-right tabular-nums">{a.member_count}</TableCell>
                          <TableCell className="text-right tabular-nums">{a.zone_count}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatAbsolute(a.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <LoadMore shown={rows.length} hasNext={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </StaffGate>
  )
}
